const ArgumentType = require('../../extension-support/argument-type');
const BlockType = require('../../extension-support/block-type');
const formatMessage = require('format-message');
const SensorPeripheral = require('../common/sensor-peripheral');
const { default: bleUtils } = require('../../util/ble');
const { BLE_CONFIG } = require('../../util/bleCharac');

/**
 * YM-K3 按键传感器模块
 * 
 * @readonly
 */
const DEVICE_OPT = {
    type: 'sensor'
};

/**
 * Manage communication with a YM-K3 peripheral over a Scratch Link client socket.
 */
class YMK3Peripheral extends SensorPeripheral {
    constructor(runtime, deviceId, originalDeviceId) {
        super(runtime, deviceId, originalDeviceId, null, BLE_CONFIG, DEVICE_OPT);
    }
}

class ScratchYMK3Device {

    get DEVICE_ID() {
        return 'YMK3';
    }

    constructor(runtime, originalDeviceId) {
        this.runtime = runtime;
        this._peripheral = new YMK3Peripheral(this.runtime, this.DEVICE_ID, originalDeviceId);
        this.bleUtils = new bleUtils();
        this.keyStates = {
            key1: false,
            key2: false,
            key3: false
        };
        // this._setupNotifyHandlers();
         //  添加：是否正在连接或已连接
         this._connectionLock = false;
         this._connectionPromise = null;
    }

    getInfo() {
        return [{
            id: 'YMK3',
            name: formatMessage({
                id: 'YMK3.categoryName',
                default: 'YM K3',
                description: 'Label for the YM K3 extension category'
            }),
            color1: "#2196F3",
            color2: "#1976D2",
            color3: "#1565C0",
            blocks: [
                {
                    opcode: 'connect',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'YMK3.ble.connect',
                        default: 'connect to YM-K3-[DEVICE_NAME]',
                        description: 'Connect to YM K3'
                    }),
                    arguments: {
                        DEVICE_NAME: {
                            type: ArgumentType.STRING,
                            defaultValue: ' ',
                        }
                    }
                },
                {
                    opcode: 'disConnect',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'YMK3.ble.disconnect',
                        default: 'disconnect from YM K3',
                        description: 'Disconnect from YM K3'
                    })
                },
                {
                    opcode: 'whenKey1Pressed',
                    blockType: BlockType.HAT,
                    text: formatMessage({
                        id: 'YMK3.whenKey1Pressed',
                        default: 'when key1 pressed',
                        description: 'When key1 is pressed'
                    })
                },
                {
                    opcode: 'whenKey2Pressed',
                    blockType: BlockType.HAT,
                    text: formatMessage({
                        id: 'YMK3.whenKey2Pressed',
                        default: 'when key2 pressed',
                        description: 'When key2 is pressed'
                    })
                },
                {
                    opcode: 'whenKey3Pressed',
                    blockType: BlockType.HAT,
                    text: formatMessage({
                        id: 'YMK3.whenKey3Pressed',
                        default: 'when key3 pressed',
                        description: 'When key3 is pressed'
                    })
                }
            ],
            menus: {}
        }];
    }

/**
     * Connect to YM-K3 device
     * @param {object} args - { DEVICE_NAME }
     */
async connect(args) {
    const deviceName = 'YM-K3-' + (args.DEVICE_NAME?.trim() || "");

    // 🔒 如果已经在连接流程中，直接返回已有 promise
    if (this._connectionPromise) {
        console.log(`Connection already in progress or completed. Skipping duplicate connect for ${deviceName}`);
        return await this._connectionPromise;
    }

    // 🔒 创建一个连接 promise 并缓存，防止后续调用再次触发 requestDevice
    this._connectionPromise = this._doConnect(deviceName).catch(err => {
        console.error('Connection failed:', err);
        // 失败后允许重试（可选）：若想禁止重试，则不清理 _connectionPromise
        // this._connectionPromise = null;
    });

    return await this._connectionPromise;
}
 /**
     * 实际执行连接逻辑（仅执行一次）
     */
 async _doConnect(deviceName) {
    // 再次检查是否已连接（避免 race condition）
    const connected = await this.isConnected();
    if (connected) {
        console.log('Already connected. Skip connection.');
        return true;
    }

    console.log(`Connecting to YM K3 device: ${deviceName}`);

    try {
        // ✅ 此处调用 bleUtils.connect，假设它封装了 requestDevice
        await this.bleUtils.connect(deviceName, BLE_CONFIG.SERVICE_UUID);
        console.log(`Connected to device: ${deviceName}`);
        await this._setupNotifyHandlers();
        return true;
    } catch (error) {
        // 清理状态，允许用户手动重试
        this._connectionPromise = null;
        throw error; // 抛出错误以便上层处理
    }
}
    async isConnected() {
        return this.bleUtils.device !== null && this.bleUtils.device.gatt.connected;
    }

    async disConnect() {
        if (this.bleUtils.device) {
            try {
                await this.bleUtils.disConnect(this.bleUtils.device);
                console.log('Disconnected from device');
                 // 🔒 断开后释放连接锁，允许重新连接
                 this._connectionPromise = null;
            } catch (error) {
                console.error('Failed to disconnect:', error);
            }
        }
    }

    async _setupNotifyHandlers() {
        if (!this.bleUtils.device || !this.bleUtils.device.gatt.connected) return;
        // Key1
        try {
            const charKey = await this.bleUtils.getCharacteristic(this.bleUtils.service, BLE_CONFIG.keyEve);
            console.log('Starting notifications for key presses...');
            // Start notifications for key presses
            await this.bleUtils.notifyCharacteristic(charKey, value => {
                const pressed = value.getUint8(0);
                console.log(`Key press state changed: ${pressed}`);
                // 使用位运算检查各个按键状态
                const key1Pressed = (pressed & 1) !== 0;  // 检查最低位
                const key2Pressed = (pressed & 2) !== 0;  // 检查第二位
                const key3Pressed = (pressed & 4) !== 0;  // 检查第三位
                
                if(key1Pressed && !this.keyStates.key1) {
                    this.keyStates.key1 = true;
                    this.runtime.startHats('YMK3_whenKey1Pressed');
                } else if (!key1Pressed) {
                    this.keyStates.key1 = false;
                }
                
                if(key2Pressed && !this.keyStates.key2) {
                    this.keyStates.key2 = true;
                    this.runtime.startHats('YMK3_whenKey2Pressed');
                } else if (!key2Pressed) {
                    this.keyStates.key2 = false;
                }
                
                if(key3Pressed && !this.keyStates.key3) {
                    this.keyStates.key3 = true;
                    this.runtime.startHats('YMK3_whenKey3Pressed');
                } else if (!key3Pressed) {
                    this.keyStates.key3 = false;
                } 
            });
        } catch (e) {}
    }

    // HAT block implementations
    whenKey1Pressed() {
        return this.keyStates.key1;
    }
    whenKey2Pressed() {
        return this.keyStates.key2;
    }
    whenKey3Pressed() {
        return this.keyStates.key3;
    }
}

module.exports = ScratchYMK3Device;
